import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  surveyReportApi,
  CreateSurveyReportDTO,
  UpdateSurveyReportDTO,
} from '../api/surveyReportApi';
import { useNotifications } from '@shared/components/notifications';

// Query keys for survey reports
export const surveyReportQueryKeys = {
  all: ['surveyReports'] as const,
  bySubject: (subjectId: string) => [...surveyReportQueryKeys.all, 'subject', subjectId] as const,
  byTenant: (tenantId: string) => [...surveyReportQueryKeys.all, 'tenant', tenantId] as const,
  detail: (id: string) => [...surveyReportQueryKeys.all, 'detail', id] as const,
};

// Create data management hooks for survey reports
export const useSurveyReportsDataManagement = (subjectId: string, _tenantId: string) => {
  const { success, error } = useNotifications();
  const queryClient = useQueryClient();

  // Query hook for getting reports by subject
  const useSurveyReportsQuery = () => {
    const query = useQuery({
      queryKey: surveyReportQueryKeys.bySubject(subjectId),
      queryFn: async () => {
        const response = await surveyReportApi.getBySubjectId(subjectId);
        // Return the reports array directly (extractHookData will access .data)
        return response.reports;
      },
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: true,
    });

    return {
      ...query,
      refetch: query.refetch,
      isFetching: query.isFetching,
    };
  };

  // Create mutation wrapper
  const useCreateSurveyReportWrapper = () => {
    return useMutation({
      mutationFn: async (data: CreateSurveyReportDTO) => {
        return await surveyReportApi.create(data);
      },
      onSuccess: (newReport) => {
        // Invalidate and refetch reports
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.bySubject(subjectId),
        });
        success(`Report "${newReport.title}" created successfully`);
      },
      onError: (err: any) => {
        error(`Failed to create report: ${err.message}`);
      },
    });
  };

  // Update mutation wrapper
  const useUpdateSurveyReportWrapper = () => {
    return useMutation({
      mutationFn: async ({ id, data }: { id: string; data: UpdateSurveyReportDTO }) => {
        return await surveyReportApi.update(id, data);
      },
      onSuccess: (updatedReport) => {
        // Invalidate and refetch reports
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.bySubject(subjectId),
        });
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.detail(updatedReport.id),
        });
        success(`Report "${updatedReport.title}" updated successfully`);
      },
      onError: (err: any) => {
        error(`Failed to update report: ${err.message}`);
      },
    });
  };

  // Delete mutation wrapper
  const useDeleteSurveyReportWrapper = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        await surveyReportApi.delete(id);
        return id;
      },
      onSuccess: (deletedId) => {
        // Invalidate and refetch reports
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.bySubject(subjectId),
        });
        // Remove from cache
        queryClient.removeQueries({
          queryKey: surveyReportQueryKeys.detail(deletedId),
        });
        success('Report deleted successfully');
      },
      onError: (err: any) => {
        error(`Failed to delete report: ${err.message}`);
      },
    });
  };

  // Note: Publish functionality removed - reports are automatically available after generation

  // Generate PDF export mutation
  const useGeneratePdfExportMutation = () => {
    return useMutation({
      mutationFn: async (reportId: string) => {
        return await surveyReportApi.generatePdfExport(reportId, {
          includeComments: true,
          includeStatistics: true,
          format: 'A4',
          orientation: 'portrait',
        });
      },
      onSuccess: (_pdfExport) => {
        // Invalidate to refresh export count
        queryClient.invalidateQueries({
          queryKey: surveyReportQueryKeys.bySubject(subjectId),
        });
        success('PDF export started. You will be notified when ready.');
      },
      onError: (err: any) => {
        error(`Failed to generate PDF: ${err.message}`);
      },
    });
  };

  return {
    useSurveyReportsQuery,
    useCreateSurveyReportWrapper,
    useUpdateSurveyReportWrapper,
    useDeleteSurveyReportWrapper,
    // usePublishReportMutation removed - no publish workflow
    useGeneratePdfExportMutation,
  };
};
